import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconClipboardCheck } from "@tabler/icons-react";
import { APPROVE_LOAN } from "apollo/mutuations";
import { LIST_MICROFINANCES, LOAN_REQUESTS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import RetailerFilter from "pages/report/retailer";
import { useEffect, useState } from "react";
import { formatNumber, formatStatus, StatusColors } from "utiles/url";

const LoanRequests = () => {
  // --- States ---
  const [openedStatusChange, setStatusChange] = useState(false);
  const [status, setStatus] = useState(null);
  const [size, setSize] = useState("10");
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [retailer, setSetRetailer] = useState(null);
  const [deleteID, setDeleteID] = useState(null);
  const [selectedMicrofinance, setSelectedMicrofinance] = useState(null);

  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // --- Queries ---

  // 1. Fetch Loan Requests
  const { data, loading, fetchMore, refetch } = useQuery(LOAN_REQUESTS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      retailer_id: parseInt(retailer),
      status: status,
      ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to get loan requests",
      });
    },
  });

  // 2. Fetch Microfinances for the Modal Dropdown
  const {
    data: microData,
    loading: loadingMicro,
    refetch: refetchMicro,
  } = useQuery(LIST_MICROFINANCES, {
    variables: {
      first: 100, // Fetch a large list to ensure the provider is visible
      page: 1,
      status: "ACTIVE",
      ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
    },
  });

  // Transform microfinance data for Mantine Select
  const microfinanceOptions =
    microData?.microfinances?.data?.map((m) => ({
      value: m.id.toString(),
      label: m.name,
    })) || [];

  // --- Mutations ---
  const [changeUserStatus, { loading: mutationLoading }] = useMutation(
    APPROVE_LOAN,
    {
      refetchQueries: [
        {
          query: LOAN_REQUESTS,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: searchValue,
            ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
          },
        },
      ],
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: `Loan request Approved successfully`,
        });
        closeApprovalModal();
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to Update Loan Request status",
        });
      },
    },
  );

  // --- Handlers ---
  const handleManualSearch = (searchTerm) => setSearchValue(searchTerm);
  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  const handleChange = (currentPage) => {
    fetchMore({
      variables: { first: parseInt(size), page: currentPage },
    });
    setActivePage(currentPage);
  };

  const handleStatus = (id) => {
    setDeleteID(id);
    setStatusChange(true);
  };

  const closeApprovalModal = () => {
    setStatusChange(false);
    setSelectedMicrofinance(null);
    setDeleteID(null);
  };

  const handleUserStatusChange = () => {
    if (!selectedMicrofinance) {
      showNotification({
        color: "yellow",
        title: "Required",
        message: "Please select a microfinance provider.",
      });
      return;
    }

    changeUserStatus({
      variables: {
        id: deleteID,
        microfinance_id: parseInt(selectedMicrofinance),
      },
    });
  };

  useEffect(() => {
    setHasMounted(true);
    refetchMicro();
    refetch();
  }, []);

  useEffect(() => {
    if (data) setTotal(data.loanRequests.paginatorInfo.lastPage);
  }, [data, size]);

  if (!hasMounted) return null;

  const headerData = [
    {
      label: "Retailer",
      key: "name_user",
      width: "20%",
      render: (rowData) => <span>{rowData.user?.name}</span>,
    },
    {
      label: "Phone",
      key: "phone",
      render: (rowData) => <span>{rowData.user?.phone}</span>,
    },
    {
      label: "Microfinance",
      key: "microfinance",
      render: (rowData) => (
        <span>{rowData.microfinance?.name || "Not Assigned"}</span>
      ),
    },
    {
      label: "End Date",
      key: "due_date",
      render: (rowData) => <span>{rowData.due_date?.split(" ")[0]}</span>,
    },
    {
      label: "Status",
      key: "status",
      render: (rowData) => (
        <span style={{ color: StatusColors[rowData.status] }}>
          {formatStatus(rowData.status)}
        </span>
      ),
    },
    {
      label: "Interest Rate",
      key: "interest_rate",
      width: "8%",
      render: (rowData) => <span>{rowData.interest_rate}</span>,
    },
    {
      label: "Amount",
      key: "amount",
      render: (rowData) => <span>{formatNumber(rowData?.amount)}</span>,
    },
    {
      label: "Actions",
      key: "actions",
      width: "20%",
      render: (rowData) => (
        <div style={{ display: "flex", gap: "6px" }}>
          {rowData.status === "PENDING" && (
            <ActionButton
              label={"Approve"}
              color={"activate"}
              onClick={() => handleStatus(rowData.id)}
            >
              <IconClipboardCheck size={15} />
            </ActionButton>
          )}
        </div>
      ),
    },
  ];

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Modal
        opened={openedStatusChange}
        onClose={closeApprovalModal}
        title="Approve Loan Request"
        centered
      >
        <Box mb="xl">
          <Text size="sm" mb="md">
            Please select a Microfinance provider to process this approval:
          </Text>
          <Select
            label="Microfinance Provider"
            placeholder="Search and select provider"
            data={microfinanceOptions}
            value={selectedMicrofinance}
            onChange={setSelectedMicrofinance}
            searchable
            nothingFound="No providers found"
            withinPortal
            disabled={loadingMicro}
          />
        </Box>
        <Group position="right">
          <Button variant="subtle" onClick={closeApprovalModal}>
            Cancel
          </Button>
          <Button
            onClick={handleUserStatusChange}
            color="blue"
            loading={mutationLoading}
            disabled={!selectedMicrofinance}
          >
            Confirm Approval
          </Button>
        </Group>
      </Modal>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            paginatorInfo={data?.loanRequests?.paginatorInfo}
            activePage={activePage}
            clearInput={clearInput}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            handleChange={handleChange}
            header={headerData}
            filterData={() => (
              <Box sx={{ display: "flex", gap: "10px", marginTop: "10px" }}>
        
                <Select
                  data={[
                    { value: "PENDING", label: "Pending" },
                    { value: "APPROVED", label: "Approved" },
                    { value: "DISAPPROVED", label: "Disapproved" },
                  ]}
                  value={status}
                  onChange={setStatus}
                  placeholder="Filter Status"
                  clearable
                />
              </Box>
            )}
            loading={loading}
            data={data ? data.loanRequests.data : []}
            handlePageSizeChange={handlePageSizeChange}
            size={size}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanRequests;
